// Omnix/client/ui/EmailComposerModal.js
import { ComponentBase } from "../core/ComponentBase.js";
export class EmailComposerModal extends ComponentBase {
    static styles = `
        .email-composer-modal {
            position: fixed;
            bottom: 0;
            right: 1rem;
            width: 550px;
            max-width: 95vw;
            height: 480px;
            max-height: 80vh;
            background-color: var(--bs-body-bg);
            border: 1px solid var(--bs-border-color);
            border-bottom: none;
            border-radius: var(--bs-border-radius-lg) var(--bs-border-radius-lg) 0 0;
            box-shadow: var(--bs-box-shadow-lg);
            display: flex;
            flex-direction: column;
            z-index: 1056; /* Above Bootstrap's modal backdrop */
            transition: all 0.3s ease-in-out;
            transform: translateY(100%); /* Start hidden */
        }
        .email-composer-modal.visible {
            transform: translateY(0);
        }
        .email-composer-modal.minimized {
            height: 42px; /* Just the header height */
            overflow: hidden;
        }
        .email-composer-modal.expanded {
            width: 90vw;
            height: 90vh;
            max-width: 1200px;
            top: 50%;
            left: 50%;
            bottom: auto;
            right: auto;
            transform: translate(-50%, -50%);
        }
        .composer-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 1rem;
            background-color: var(--bs-tertiary-bg);
            cursor: grab;
            border-bottom: 1px solid var(--bs-border-color-translucent);
        }
        .composer-header:active { cursor: grabbing; }
        .composer-title { font-weight: 500; }
        .composer-window-controls .btn { padding: 0.1rem 0.4rem; font-size: 1.2rem; }
        .composer-body { flex-grow: 1; display: flex; flex-direction: column; overflow: hidden; }
        .composer-recipients { padding: 0.5rem 1rem; border-bottom: 1px solid var(--bs-border-color-translucent); }
        .recipient-row { display: flex; align-items: center; gap: 0.5rem; }
        .recipient-row label { flex: 0 0 30px; color: var(--bs-secondary-color); }
        .recipient-row input { border: none; outline: none; flex-grow: 1; background: transparent; }
        .cc-bcc-toggle { color: var(--bs-secondary-color); cursor: pointer; font-size: 0.9rem; }
        .cc-bcc-fields { display: none; }
        .cc-bcc-fields.visible { display: block; }
        .subject-row input { width: 100%; border: none; outline: none; padding: 0.75rem 1rem; font-weight: 500; background: transparent; }
        .email-body-editor {
            flex-grow: 1;
            overflow-y: auto;
            padding: 1rem;
            outline: none;
        }
        .composer-footer {
            padding: 0.5rem 1rem;
            border-top: 1px solid var(--bs-border-color);
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        .formatting-toolbar .btn { color: var(--bs-secondary-color); }
        @media (max-width: 768px) {
            .email-composer-modal { /* Always fullscreen on mobile */
                width: 100vw; height: 100vh; max-width: 100vw; max-height: 100vh;
                top: 0; right: 0; border-radius: 0; transform: translateY(0) !important;
            }
        }
    `;

    constructor(config) {
        super(config);
        this.isVisible = false;
        this.isExpanded = false;
        this.isMinimized = false;
        this.showCcBcc = false;
        this.attachments = [];
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.id = `${this.config.instanceId}-container`;
        this.fileUpload = await this.createManagedComponent('FileUpload', null, {
            instanceId: `${this.config.instanceId}-uploader`,
            hidden: true,
            // getUploadKey logic will be set dynamically in show()
        });
        this.fileUpload.on('uploadCompleted', () => this.handleFilesUploaded());
        await super.initialize();
    }

    render() {
        this.container.className = `email-composer-modal ${this.isVisible ? 'visible' : ''} ${this.isExpanded ? 'expanded' : ''} ${this.isMinimized ? 'minimized' : ''}`;
        this.container.innerHTML = `
            <div class="composer-header">
                <span class="composer-title">New Message</span>
                <div class="composer-window-controls">
                    <button class="btn btn-link" data-action="minimize"><i class="bi bi-dash-lg"></i></button>
                    <button class="btn btn-link" data-action="expand"><i class="bi bi-arrows-angle-expand"></i></button>
                    <button class="btn btn-link" data-action="close"><i class="bi bi-x-lg"></i></button>
                </div>
            </div>
            <div class="composer-body">
                <div class="composer-recipients">
                    <div class="recipient-row">
                        <label for="email-to">To</label>
                        <input type="text" id="email-to" name="to">
                        <a href="#" class="cc-bcc-toggle" data-action="toggle-cc-bcc">Cc/Bcc</a>
                    </div>
                    <div class="cc-bcc-fields ${this.showCcBcc ? 'visible' : ''}">
                        <div class="recipient-row mt-1"><label for="email-cc">Cc</label><input type="text" id="email-cc" name="cc"></div>
                        <div class="recipient-row mt-1"><label for="email-bcc">Bcc</label><input type="text" id="email-bcc" name="bcc"></div>
                    </div>
                </div>
                <div class="subject-row"><input type="text" name="subject" placeholder="Subject"></div>
                <div class="email-body-editor" contenteditable="true"></div>
                <div class="attachment-previews p-2"></div>
            </div>
            <div class="composer-footer">
                <button class="btn btn-primary" data-action="send">Send</button>
                <div class="formatting-toolbar btn-group">
                    <button class="btn" data-command="bold"><i class="bi bi-type-bold"></i></button>
                    <button class="btn" data-command="italic"><i class="bi bi-type-italic"></i></button>
                    <button class="btn" data-command="insertUnorderedList"><i class="bi bi-list-ul"></i></button>
                    <button class="btn" data-action="attach"><i class="bi bi-paperclip"></i></button>
                </div>
            </div>
        `;
        this.attachEventListeners();
        return this.container;
    }

    attachEventListeners() {
        // Window controls
        this.container.querySelector('[data-action="minimize"]').addEventListener('click', () => this.toggleMinimize());
        this.container.querySelector('[data-action="expand"]').addEventListener('click', () => this.toggleExpand());
        this.container.querySelector('[data-action="close"]').addEventListener('click', () => this.hide());
        this.container.querySelector('[data-action="toggle-cc-bcc"]').addEventListener('click', (e) => {
            e.preventDefault();
            this.showCcBcc = !this.showCcBcc;
            this.container.querySelector('.cc-bcc-fields').classList.toggle('visible', this.showCcBcc);
        });
        this.container.querySelector('[data-action="send"]').addEventListener('click', () => this.handleSend());
        this.container.querySelector('[data-action="attach"]').addEventListener('click', () => this.fileUpload.openFile());

        // Formatting
        this.container.querySelectorAll('.formatting-toolbar [data-command]').forEach(btn => {
            btn.addEventListener('click', () => document.execCommand(btn.dataset.command, false, null));
        });
    }

    async show(action, interaction, conversation) { // The 'conversation' object is now crucial
        this.isVisible = true;
        document.body.appendChild(this.container);
        this.render();

        this.fileUpload.setConfig({
            getUploadKey: async () => this.appClient.rpcClient.call('UnifiedInboxRpc.getAttachmentUploadKey', {
                conversationId: conversation._id
            })
        });

        const initialData = this.prepareInitialData(action, interaction, conversation);

        this.container.querySelector('[name="to"]').value = initialData.to;
        this.container.querySelector('[name="cc"]').value = initialData.cc;
        this.container.querySelector('[name="subject"]').value = initialData.subject;
        this.container.querySelector('.email-body-editor').innerHTML = initialData.body;
        this.container.querySelector('.composer-title').textContent = initialData.title;
    }


    hide() {
        this.isVisible = false;
        this.container.classList.remove('visible');
        setTimeout(() => {
            if (this.container.parentNode) {
                this.container.parentNode.removeChild(this.container);
            }
            this.emit('closed');
        }, 300); // Wait for transition
    }

    toggleMinimize() {
        this.isMinimized = !this.isMinimized;
        this.container.classList.toggle('minimized', this.isMinimized);
    }

    toggleExpand() {
        this.isExpanded = !this.isExpanded;
        this.container.classList.toggle('expanded', this.isExpanded);
        this.container.querySelector('[data-action="expand"] i').className = this.isExpanded ? 'bi bi-arrows-angle-contract' : 'bi bi-arrows-angle-expand';
    }

    prepareInitialData(action, interaction, conversation) { // Add 'conversation' parameter
        let data = { to: '', cc: '', bcc: '', subject: '', body: '', title: 'New Message' };
        if (action === 'compose') {
            const contactEmailChannel = conversation.contact?.channels?.find(ch => ch.type === 'email');
            data.to = contactEmailChannel?.value || conversation.contact?.email || '';
            data.subject = '';
            data.body = '<br><br>--<br>Sent from Omnix'; // Optional signature
            data.title = 'Compose Email';
            return data;
        }
        const originalSender = interaction.payload.from;
        const quote = `<br><br><hr>On ${new Date(interaction.timestamp).toLocaleString()}, ${originalSender} wrote:<blockquote style="border-left: 2px solid #ccc; padding-left: 1em; margin-left: 0.5em;">${interaction.payload.body}</blockquote>`;

        switch (action) {
            case 'reply':
                data.to = originalSender;
                data.subject = `Re: ${interaction.payload.subject}`;
                data.body = quote;
                data.title = "Reply";
                break;
            case 'replyAll':
                data.to = originalSender;
                data.cc = (interaction.payload.cc || []).join(', ');
                data.subject = `Re: ${interaction.payload.subject}`;
                data.body = quote;
                data.title = "Reply All";
                break;
            case 'forward':
                data.subject = `Fwd: ${interaction.payload.subject}`;
                data.body = quote;
                data.title = "Forward";
                break;
        }
        return data;
    }


    async handleSend() {
        const to = this.container.querySelector('[name="to"]').value;
        const subject = this.container.querySelector('[name="subject"]').value;
        const body = this.container.querySelector('.email-body-editor').innerHTML;

        if (!to || !subject) {
            this.appClient.notifier.error("Recipient and Subject are required.");
            return;
        }

        this.emit('emailSendRequested', {
            channelType: 'email',
            content: {
                subject: subject,
                body: body,
                to: to.split(',').map(s => s.trim()),
                cc: this.container.querySelector('[name="cc"]').value.split(',').map(s => s.trim()),
                bcc: this.container.querySelector('[name="bcc"]').value.split(',').map(s => s.trim()),
            },
            attachments: this.attachments
        });
        this.hide();
    }

    // Attachment handling methods would go here, similar to DynamicComposer
}